/**
 * FreezingResultsFunnel — Visualization of egg/embryo attrition.
 *
 * "35 eggs → 28 mature → 25 fertilized → 18 Day 3 → 8 blastocysts → 6 euploid"
 */

import { View } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import { CardSubtitleText } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import { type FunnelStage } from "./funnelData";

export {
  buildEggFreezingFunnel,
  buildEmbryoFreezingFunnel,
} from "./funnelData";

const FUNNEL_STAGE_LABEL_KEYS: Record<string, string> = {
  "Eggs Retrieved": "cryo_vault.funnel.retrieved",
  "Mature (MII)": "cryo_vault.funnel.mature",
  Fertilized: "cryo_vault.funnel.fertilized",
  Blastocysts: "cryo_vault.funnel.blastocysts",
  Frozen: "cryo_vault.funnel.frozen",
  "Embryos Frozen": "cryo_vault.funnel.embryos_frozen",
  "Euploid (PGT-A)": "cryo_vault.funnel.euploid",
  "No Result": "cryo_vault.funnel.no_result",
};

interface FreezingResultsFunnelProps {
  /** Stages in order, e.g. [{label: "Eggs Retrieved", count: 35}, ...] */
  stages: FunnelStage[];
}

export function FreezingResultsFunnel({ stages }: FreezingResultsFunnelProps) {
  const t = useTranslations();
  if (stages.length === 0) return null;

  const totalDots = Math.max(stages[0]?.count ?? 1, 1);
  const rows: OutcomeDotFunnelStage[] = stages.map((stage, index) => {
    const previous = stages[index - 1]?.count;
    const share =
      index === 0 || !previous
        ? null
        : Math.round((stage.count / (stages[0]?.count || 1)) * 100);
    const labelKey = FUNNEL_STAGE_LABEL_KEYS[stage.label];
    return {
      id: `${stage.label}-${index}`,
      label: labelKey ? t(labelKey) : stage.label,
      value: stage.count,
      detail:
        stage.detail ??
        (index === 0
          ? t("cryo_vault.funnel.recorded_detail")
          : t("cryo_vault.funnel.share_detail", {
              params: { share: String(share ?? 0) },
            })),
    };
  });

  return (
    <View
      style={{
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(232, 224, 216, 0.6)",
        backgroundColor: "rgba(255, 255, 255, 0.3)",
        padding: 16,
      }}
    >
      <Text
        style={{
          fontSize: 13,
          fontWeight: "500",
          color: "#5A4D45",
          marginBottom: 12,
        }}
      >
        {t("cryo_vault.funnel.title")}
      </Text>
      <CardSubtitleText style={{ marginBottom: 12 }}>
        {t("cryo_vault.funnel.observed_subtitle")}
      </CardSubtitleText>
      <OutcomeDotFunnelRows stages={rows} totalDots={totalDots} embedded />
    </View>
  );
}
